if (!customElements.get('media-gallery')) {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');

      try {
        if (this.elements.viewer && typeof debounce === 'function') {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }
      } catch (e) { console.error('MediaGallery: debounce or viewer error', e); }

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (btn) {
            btn.addEventListener('click', (evt) => {
              evt.preventDefault();
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          }
        });
      }

      if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    connectedCallback() {
      // Small delay to ensure all custom elements (like slider-component) are defined and rendered
      const initScroll = () => {
        const activeMedia = this.elements.viewer.querySelector('.is-active');
        if (activeMedia) {
          console.log('MediaGallery: Initial scroll to active media');
          this.setActiveMedia(activeMedia.dataset.mediaId, false);
        }
      };
      
      window.requestAnimationFrame(initScroll);
      // Extra fallback for slow mobile rendering
      setTimeout(initScroll, 1000);
      setTimeout(initScroll, 2500);

      // Auto-play video if the initially active slide is a video
      setTimeout(() => {
        const activeMedia = this.elements.viewer.querySelector('.is-active');
        if (activeMedia) this.playActiveMedia(activeMedia);
      }, 800);
    }

    onSlideChanged(event) {
      if (!this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
      this.playActiveMedia(event.detail.currentElement);
    }

    setActiveMedia(mediaId, prepend) {
      console.log('MediaGallery: setActiveMedia called with', mediaId);
      if (!this.elements.viewer) return;
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      console.log('MediaGallery: target media element found?', !!activeMedia);
      if (!activeMedia) return;

      // Restore all slides visibility (in case a video previously hid them)
      this.restoreAllSlides();

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      // Immediately trigger video autoplay if the active slide has a video
      window.pauseAllMedia();
      setTimeout(() => this.playActiveMedia(activeMedia), 50);

      this.preventStickyHeader();
      
      const scrollAnimation = () => {
        try {
          const viewerSlider = this.elements.viewer.querySelector('ul') || this.elements.viewer.slider || this.elements.viewer.querySelector('.slider');
          if (viewerSlider && activeMedia) {
            console.log('MediaGallery: Navigating to', activeMedia.dataset.mediaId);
            
            // scrollIntoView is much more reliable for RTL and mixed orientations
            activeMedia.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest', 
              inline: 'start' 
            });
            
            // Secondary trigger after animation
            setTimeout(() => this.playActiveMedia(activeMedia), 600);
          } else {
            this.playActiveMedia(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
            if (activeThumbnail) {
               activeThumbnail.scrollIntoView({ 
                 behavior: 'smooth', 
                 block: 'nearest', 
                 inline: 'start' 
               });
            }
          }
        } catch (e) { 
          console.error('MediaGallery: Navigation failed', e);
          this.playActiveMedia(activeMedia);
        }
      };

      // Call scroll animation
      setTimeout(scrollAnimation, 50);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (!activeThumbnail) return;

      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail.dataset.mediaPosition) {
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      const btn = thumbnail.querySelector('button');
      if (btn) btn.setAttribute('aria-current', true);
      
      if (typeof this.elements.thumbnails.isSlideVisible === 'function' && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      const thumbnailSlider = this.elements.thumbnails.querySelector('ul') || this.elements.thumbnails.slider;
      if (thumbnailSlider) {
        thumbnail.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      if (!activeItem) return;
      
      const deferredMedia = activeItem.querySelector('deferred-media');
      if (!deferredMedia) return;

      console.log('MediaGallery: Force-playing', activeItem.id);

      // 1. Force the active slide and deferred container to be visible and correctly positioned
      activeItem.style.setProperty('display', 'block', 'important');
      activeItem.classList.add('is-active');

      deferredMedia.style.setProperty('display', 'block', 'important');
      deferredMedia.style.setProperty('opacity', '1', 'important');
      deferredMedia.style.setProperty('visibility', 'visible', 'important');
      deferredMedia.style.setProperty('position', 'absolute', 'important');
      deferredMedia.style.setProperty('top', '0', 'important');
      deferredMedia.style.setProperty('left', '0', 'important');
      deferredMedia.style.setProperty('width', '100%', 'important');
      deferredMedia.style.setProperty('height', '100%', 'important');
      deferredMedia.style.setProperty('z-index', '10', 'important');
      deferredMedia.style.setProperty('background', '#000', 'important'); // Black background for video visibility

      // 2. Load Content
      if (typeof deferredMedia.loadContent === 'function' && !deferredMedia.getAttribute('loaded')) {
        deferredMedia.loadContent(false);
      }

      // 3. Play Trigger
      const attemptPlay = () => {
        const video = deferredMedia.querySelector('video');
        if (video) {
          video.muted = true;
          video.setAttribute('muted', '');
          video.style.setProperty('object-fit', 'contain', 'important');
          video.style.setProperty('width', '100%', 'important');
          video.style.setProperty('height', '100%', 'important');
          video.loop = true;
          video.setAttribute('loop', '');
          
          video.play()
            .then(() => {
              console.log('MediaGallery: Video success, hiding overlays');
              const modalOpener = activeItem.querySelector('.product__modal-opener');
              if (modalOpener) {
                  modalOpener.style.setProperty('opacity', '0', 'important');
                  modalOpener.style.setProperty('pointer-events', 'none', 'important');
              }
            })
            .catch((e) => console.warn('MediaGallery: video.play() error:', e));
        }
        
        const iframe = deferredMedia.querySelector('iframe');
        if (iframe) {
          const cmd = iframe.classList.contains('js-youtube') 
            ? '{"event":"command","func":"playVideo","args":""}' 
            : '{"method":"play"}';
          iframe.contentWindow.postMessage(cmd, '*');
          
          setTimeout(() => {
            const modalOpener = activeItem.querySelector('.product__modal-opener');
            if (modalOpener) modalOpener.style.opacity = '0';
          }, 1000);
        }
      };

      setTimeout(attemptPlay, 250);
    }

    // Restore all slides visibility when switching back to an image slide
    restoreAllSlides() {
      const allSlides = this.elements.viewer.querySelectorAll('[data-media-id]');
      allSlides.forEach((slide) => {
        slide.style.removeProperty('display');
        slide.style.removeProperty('opacity');
        slide.style.removeProperty('z-index');
        slide.classList.remove('is-active');

        // Restore modal-opener visibility
        const modalOpener = slide.querySelector('.product__modal-opener');
        if (modalOpener) {
          modalOpener.style.removeProperty('opacity');
          modalOpener.style.removeProperty('pointer-events');
        }
        
        // Restore deferred-media default display
        const dm = slide.querySelector('.deferred-media');
        if (dm) dm.style.removeProperty('display');
      });
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
